import api from "./api";

import {
  getCurrentUser as getStoredCurrentUser,
  requestCurrentUserRefresh,
  setCurrentUser,
} from "../utils/permissions";

import {
  clearAuthSession,
} from "../utils/authSession";

let activeRefreshPromise = null;

const extractCurrentUser = (payload) => {
  return (
    payload?.user ||
    payload?.data?.user ||
    payload?.data ||
    payload ||
    null
  );
};

const normalizeCurrentUser = (user) => {
  if (!user || typeof user !== "object" || !user.id) {
    const error = new Error(
      "The server did not return a valid current user"
    );

    error.statusCode = 500;
    throw error;
  }

  return {
    ...user,
    permissions:
      user.permissions === "ALL"
        ? "ALL"
        : user.permissions &&
            typeof user.permissions === "object" &&
            !Array.isArray(user.permissions)
          ? user.permissions
          : {},
  };
};

const invalidateCurrentSession = () => {
  clearAuthSession();
  setCurrentUser(null);
};

export const loginUser = async (email, password) => {
  const res = await api.post("/auth/login", {
    email,
    password,
  });

  return res.data;
};

/*
 * Kept compatible with existing callers:
 * this returns the complete /auth/me response body, usually { user }.
 */
export const getCurrentUser = async () => {
  const res = await api.get("/auth/me");
  return res.data;
};

/*
 * De-duplicated current-user refresh used by ProtectedRoute.
 * Concurrent focus, timer, and custom-event refreshes share one request.
 */
export const refreshCurrentUserSession = async () => {
  if (activeRefreshPromise) {
    return activeRefreshPromise;
  }

  activeRefreshPromise = (async () => {
    try {
      const payload = await getCurrentUser();
      const freshUser = normalizeCurrentUser(
        extractCurrentUser(payload)
      );

      setCurrentUser(freshUser);
      return freshUser;
    } catch (error) {
      const status =
        error?.response?.status ||
        error?.statusCode ||
        null;

      /*
       * /auth/me returning 401, 403, or 404 means the session is no longer
       * usable: token invalid, account disabled, role disabled, or user removed.
       */
      if ([401, 403, 404].includes(status)) {
        invalidateCurrentSession();
      }

      throw error;
    } finally {
      activeRefreshPromise = null;
    }
  })();

  return activeRefreshPromise;
};

export const refreshCurrentUserFromUi = () => {
  requestCurrentUserRefresh();
};

export const getCachedCurrentUser = () =>
  getStoredCurrentUser();
